/**
 * What a copy of whole script blocks puts on the clipboard, and how a paste
 * reads it back. Pure — ScriptView owns the clipboard events, this owns the
 * format.
 *
 * TWO FLAVOURS, ON PURPOSE. `text/plain` is the one every other app can read
 * (a mail, a doc, another editor): one block per line, which is exactly how the
 * script reads on the page. But plain text cannot say where a block ENDED,
 * because a block may itself contain newlines — Shift+Enter puts them there —
 * so pasting three copied blocks back would quietly become five. The private
 * `SCRIPT_BLOCKS_MIME` flavour carries the array itself, so a copy that started
 * in the script and lands back in it reproduces the blocks it came from,
 * exactly. Both are written on every copy; a paste prefers the private one and
 * falls back to splitting the text.
 */

/**
 * Private clipboard flavour: a JSON array of block texts. Chromium (and so
 * Electron) round-trips arbitrary flavours through the system clipboard within
 * the app; anywhere it does not survive, `text/plain` still does.
 */
export const SCRIPT_BLOCKS_MIME = "application/x-kola-script-blocks";

/** The `text/plain` form of a block copy: one block per line. */
export function blocksToText(blocks: readonly string[]): string {
  return blocks.join("\n");
}

/** The `SCRIPT_BLOCKS_MIME` form. */
export function encodeBlocks(blocks: readonly string[]): string {
  return JSON.stringify(blocks);
}

/**
 * Read the private flavour back. Null for anything that is not our own array of
 * strings — an empty flavour, another app's data, a truncated payload — and the
 * caller then falls back to `textToBlocks`.
 */
export function decodeBlocks(data: string | null | undefined): string[] | null {
  if (data === undefined || data === null || data === "") return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(data);
  } catch {
    return null;
  }
  if (!Array.isArray(parsed) || parsed.length === 0) return null;
  if (!parsed.every((entry): entry is string => typeof entry === "string")) return null;
  return parsed;
}

/**
 * Plain text → the blocks it should become: one per line, every EOL convention
 * normalized first (a Windows `.txt` and a Mac-classic export both paste the
 * same way).
 *
 * ONE trailing empty line is dropped, and only one: text copied out of an
 * editor almost always ends in a newline, and honouring it would leave an empty
 * block below every paste. Two trailing newlines are a deliberate blank line,
 * so that one is kept.
 */
export function textToBlocks(text: string): string[] {
  const lines = text.replaceAll("\r\n", "\n").replaceAll("\r", "\n").split("\n");
  if (lines.length > 1 && lines[lines.length - 1] === "") lines.pop();
  return lines;
}
